import api from './api';
import type { Supplier, PaginatedResponse } from '../types/venue';
import type { SupplierItemLink } from '../types/supplierItemLink';

/**
 * venueSupplierService — read-only supplier access scoped to a single venue.
 *
 * Hits /admin/venue/:venueId/suppliers, which returns only suppliers the venue
 * has actually transacted with (via item-link, PO, or invoice). Used by:
 *   - VenueSupplierList / VenueSupplierDetail pages
 *   - Supplier dropdowns inside the venue workspace (PurchaseOrderForm,
 *     QuickReceiveModal, InventoryItemDetail "Add Supplier")
 *
 * Global supplier endpoints (`supplierService`) remain admin-only.
 *
 * SUPPLIER-CONNECT.1 adds the one write here: whether this venue may DEAL with
 * an existing global supplier. That is a statement about the venue, not the
 * supplier, which is why it lives on the venue tree — and it is admin-only,
 * because a venue user granting themselves access would defeat the point of not
 * exposing the global list.
 */

const base = (venueId: string) => `/admin/venue/${venueId}/suppliers`;

const venueSupplierService = {
  listVenueSuppliers: async (
    venueId: string,
    page = 1,
    pageSize = 20,
    q?: string,
    type?: string,
    sort = 'name',
    order: 'asc' | 'desc' = 'asc',
  ): Promise<PaginatedResponse<Supplier>> => {
    const params: Record<string, unknown> = { page, pageSize, sort, order };
    if (q?.trim()) params.q = q.trim();
    if (type) params.type = type;
    const response = await api.get(base(venueId), { params });
    return response.data;
  },

  getVenueSupplier: async (venueId: string, supplierId: string): Promise<Supplier> => {
    const response = await api.get(`${base(venueId)}/${supplierId}`);
    return response.data;
  },

  /**
   * Item-links for one supplier within this venue. 404-equivalent (empty array)
   * if there are none. Never returns links from another venue.
   */
  getVenueSupplierItemLinks: async (
    venueId: string,
    supplierId: string,
  ): Promise<SupplierItemLink[]> => {
    const response = await api.get(`${base(venueId)}/${supplierId}/item-links`);
    return response.data;
  },

  /**
   * Grant this venue access to an existing global supplier.
   *
   * Idempotent — connecting an already-connected supplier returns 200 rather
   * than erroring, so a double-click is not a failure.
   */
  connectSupplier: async (
    venueId: string,
    supplierId: string,
  ): Promise<{ connected: boolean; supplierName: string }> => {
    const response = await api.post(`${base(venueId)}/${supplierId}/connect`);
    return response.data;
  },

  /**
   * Withdraw the grant.
   *
   * `stillVisible` comes back true when the venue has real purchase history
   * with the supplier: the derived set keeps it, because a grant withdrawn
   * today cannot un-buy last month's wine. Surface that rather than letting
   * someone conclude the call failed.
   */
  disconnectSupplier: async (
    venueId: string,
    supplierId: string,
  ): Promise<{ connected: boolean; stillVisible: boolean; reason?: string }> => {
    const response = await api.delete(`${base(venueId)}/${supplierId}/connect`);
    return response.data;
  },
};

export default venueSupplierService;
